import type { LegacyRef, ReactNode } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UIkit from 'uikit';
import InfosDecorator from './InfosDecorator';
import { getOrElse } from '../../utils/functions';
import type {
  ApiReturnTag,
  ApiReturnTextResource,
} from '@highimpact-frontend/api-types';
import ConfirmModal from '../../components/utils/confirmModal/ConfirmModal';
import ResourceService from '../../api/services/config/resource/ImageResourceService';
import type { ApiReturnResource } from '../../api/services/config/tags/ITagService';
import EditRessource from '../../components/ressources/EditRessource';
import ActionList from '../../components/utils/actionListComponent/ActionList';
import resourceService from '../../api/services/config/resource/ResourceService';
import type { ApiTag } from '../../api/services/config/tags/TagService';
import { useModal } from '../../utils/hooks/useModal';
import ManagementDecorator from './ManagementDecorator';

interface MetadataDisplayerProps {
  data?: Map<ApiReturnTextResource, ApiReturnTag[]>;
}

function MetadataDisplayer(
  _props: MetadataDisplayerProps,
  ref: LegacyRef<HTMLDivElement>
): ReactNode {
  const [resources, setResources] = useState<ApiReturnTextResource[]>([]);

  const editModal = useModal<{
    resource: ApiReturnResource | null;
    tags: ApiTag[] | undefined;
  }>();
  const deleteModal = useModal<{
    callback: () => void;
    message: string;
    target: string;
    metadataId: number;
  }>();

  const navigate = useNavigate();

  const refresh = () => {
    navigate(0);
  };

  const correspondencesTypes: Record<string, string> = {
    text: 'Texte',
    blob: 'fichier',
  };

  // Retrieve parameters from the URL
  const { project_id, site_id, device_id } = useParams<{
    project_id: string | undefined;
    site_id: string | undefined;
    device_id?: string | undefined;
  }>();

  const params = {
    project_id: project_id ? Number.parseInt(project_id) : null,
    site_id: site_id ? Number.parseInt(site_id) : null,
    device_id: device_id ? Number.parseInt(device_id) : null,
  };

  const headings = ['Nom', 'Type', 'Valeur', 'Tags', 'Actions'] as const;
  type RowKey = (typeof headings)[number];
  type Row = { [key in RowKey]: string } & { resource: ApiReturnResource };

  const deleteResource = async (id: number) => {
    try {
      const response = await ResourceService.delete(id);
      console.log('Success:', response);
      //FIXME: use dispatch.
      setTimeout(refresh, 2000);
      UIkit.notification({
        message: 'La ressource a été supprimée avec succès',
        status: 'success',
      });
    } catch (error: any) {
      UIkit.notification({
        message:
          error.response?.data?.message ||
          'Une erreur est survenue lors de la suppression de la ressource.',
        status: 'danger',
      });
      console.error('Error:', error);
    }
  };

  const createRow = (resource: ApiReturnTextResource): Row => ({
    Nom: getOrElse(resource.name),
    Type: getOrElse(resource.resource_type),
    Valeur: getOrElse(resource.value),
    Tags:
      resource.tag_names!.length > 0
        ? resource.tag_names!.map((tag) => tag).join(', ')
        : '--',
    Actions: String(resource.id),
    resource,
  });

  // Generate rows from data
  const rows: Row[] = resources.map((resource) => createRow(resource));

  useEffect(() => {
    resourceService
      .getByEntity(params)
      .then((response) => setResources(response));
  }, [site_id, project_id]);

  return ManagementDecorator(ref, () => (
    <InfosDecorator>
      <div>
        <table className="uk-table uk-table-divider">
          <thead>
            <tr>
              {headings.map((val, index) => (
                <th
                  key={index}
                  style={val === 'Valeur' ? { textAlign: 'center' } : {}}
                >
                  {val}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>{row.Nom}</td>
                <td>{correspondencesTypes[row.Type]}</td>
                <td style={{ display: 'flex', justifyContent: 'center' }}>
                  {row.Type === 'text' ? (
                    row.Valeur
                  ) : row.Type === 'blob' ? (
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '15px',
                      }}
                    >
                      <img
                        className="image"
                        src={`data:image/png;base64,${row.Valeur}`}
                        alt={row.Nom || 'Image'}
                      />
                    </div>
                  ) : (
                    row.Nom || 'Non spécifié'
                  )}
                </td>
                <td>{row.Tags}</td>
                <td>
                  <ActionList
                    actions={[
                      {
                        name: 'delete',
                        onclick: () =>
                          deleteModal.open({
                            callback: () => deleteResource(row.resource.id),
                            message: 'Voulez-vous supprimer la ressource',
                            target: row.resource.name,
                            metadataId: row.resource.id,
                          }),
                      },
                      {
                        name: 'update',
                        onclick: () =>
                          editModal.open({
                            resource: row.resource,
                            tags: row.resource.tag_names?.map((t) => ({
                              name: t,
                              is_generic: false,
                            })),
                          }),
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ConfirmModal
          onCancel={deleteModal.close}
          onConfirm={() => deleteModal.params?.callback}
          open={deleteModal.isOpen}
        >
          <p>
            {deleteModal.params?.message!}{' '}
            <span className="uk-text-bold">{deleteModal.params?.target}</span> ?
          </p>
        </ConfirmModal>
        {editModal.isOpen && (
          <EditRessource
            handleClose={editModal.close}
            isModalOpen={editModal.isOpen}
            resource={editModal.params?.resource!}
            tags={editModal.params?.tags!}
          />
        )}
      </div>
    </InfosDecorator>
  ));
}

export default forwardRef(MetadataDisplayer);
